import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe, DecimalPipe } from '@angular/common';
import { OverlayModule } from '@angular/cdk/overlay';
import { MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { ReviewExportData } from '@models/online-reputation.models';
import { ReviewsService } from '@core/services/reviews.service';
import {
  buildPeriodFilterPill,
  buildSelectedPills,
  FilterStore,
  NetworkFilterPill,
  PeriodFilterPill
} from '@core/stores/filter.store';
import { REVIEW_EXPORT_DOWNLOAD } from '@core/stores/review-export.store';
import { FilterPillsComponent } from '@shared/components/filter/filter-pills/filter-pills.component';

export type ExportHistoryRow = {
  export: ReviewExportData;
  // unset when the export's filter couldn't be read back
  periodPill: PeriodFilterPill | null;
  networkPills: NetworkFilterPill[] | null;
};

/**
 * The user's download history: every export they can still download, newest first, with the
 * filter each was made with (on hover of its "i") and a download action. Loaded on every open, so a
 * just-finished export is always there. See openspec/changes/reviews-export-history.
 */
@Component({
  selector: 'app-export-history-dialog',
  templateUrl: './export-history-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatTableModule,
    MatProgressBar,
    OverlayModule,
    DatePipe,
    DecimalPipe,
    TranslatePipe,
    FilterPillsComponent
  ]
})
export class ExportHistoryDialogComponent {
  readonly displayedColumns = ['createdAt', 'fileName', 'reviews', 'expiresAt', 'info', 'download'];

  readonly loading = signal(true);
  readonly loadFailed = signal(false);
  readonly rows = signal<ExportHistoryRow[]>([]);
  // the export whose download link is being fetched
  readonly downloadingId = signal<string | undefined>(undefined);
  // the export whose filter popover is open
  readonly infoOpenId = signal<string | undefined>(undefined);

  readonly dialogRef = inject<MatDialogRef<ExportHistoryDialogComponent>>(MatDialogRef);
  private readonly reviewsService = inject(ReviewsService);
  private readonly filterStore = inject(FilterStore);
  private readonly download = inject(REVIEW_EXPORT_DOWNLOAD);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.reviewsService.listExports()
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (exports) => this.rows.set(exports.map((e) => this.toRow(e))),
        error: () => this.loadFailed.set(true)
      });
  }

  showInfo(id: string | undefined): void {
    this.infoOpenId.set(id);
  }

  hideInfo(id: string | undefined): void {
    if (this.infoOpenId() === id) {
      this.infoOpenId.set(undefined);
    }
  }

  /**
   * Downloads the export through a fresh signed link, keeping the dialog open. One that expired
   * since the list was loaded (410, or 404 once it's gone) is dropped from the table.
   */
  downloadExport(row: ExportHistoryRow): void {
    const id = row.export.id;
    if (!id || this.downloadingId()) {
      return;
    }
    this.downloadingId.set(id);
    this.reviewsService.getExportDownload(id)
      .pipe(
        finalize(() => this.downloadingId.set(undefined)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (link) => {
          if (link.url) {
            this.download(link.url);
          }
        },
        error: (err: unknown) => {
          const status = err instanceof HttpErrorResponse ? err.status : undefined;
          if (status === 410 || status === 404) {
            this.rows.update((rows) => rows.filter((r) => r.export.id !== id));
            this.snackBar.open(this.translate.instant('app.review.export.history.expired'));
          } else {
            this.snackBar.open(this.translate.instant('app.review.export.history.downloadFailed'));
          }
        }
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  private toRow(data: ReviewExportData): ExportHistoryRow {
    const filter = data.filter;
    if (!filter?.network || !filter?.period) {
      return { export: data, periodPill: null, networkPills: null };
    }
    return {
      export: data,
      periodPill: buildPeriodFilterPill(filter.period, { withDates: true }),
      networkPills: buildSelectedPills(filter.network, this.filterStore.options().agentsMap)
    };
  }
}
